/**
 * The line under the chart row that names what happened to the charts.
 *
 * Three cases, and the middle one is the reason this exists as a function
 * rather than a nested ternary in the page. A collection can have no generated
 * charts, or charts that all failed to draw, or, most often in practice, some
 * that drew and some that did not. `isUnplottableSeries` suppresses a month
 * series with a single bucket, so `Projects` declared three charts, rendered
 * two, and said nothing about the third: the visitor saw a 2-card row with no
 * hint that `Projects added over time` existed.
 *
 * Naming the suppressed charts is the same promise `5e85376` made for a
 * chartless table, extended to the partial case. An empty month series is a
 * refusal, not a failed query: stay silent when every suppressed chart is
 * month-bucketed and at least one chart still drew. Returns null when every
 * declared chart drew, because then there is nothing to explain.
 */
export function chartRowStatus(args: {
  /** Every chart the generator declared for this collection. */
  declared: { id: string; title?: string; name?: string; bucket?: string }[];
  /** Ids that reported back as unplottable. */
  unplottable: Record<string, boolean>;
}): string | null {
  const { declared, unplottable } = args;

  if (!declared.length) return "No generated charts for this table.";

  // A card only learns it has nothing to draw after its own series query
  // resolves, so hold the copy until every declared chart has reported.
  // Otherwise first paint flashes a "could not be drawn" line under charts
  // that are still loading.
  const allReported = declared.every(
    (chart) => typeof unplottable[chart.id] === "boolean",
  );
  if (!allReported) return null;

  const suppressed = declared.filter((chart) => unplottable[chart.id]);
  if (!suppressed.length) return null;

  if (suppressed.length === declared.length) {
    // Every chart empty — often a month series with no parseable dates.
    if (suppressed.every((chart) => chart.bucket === "month")) {
      return "No month buckets could be drawn for this table.";
    }
    return "Charts could not be drawn for this table.";
  }

  // An empty month series beside real figures is a refusal, not a failed
  // query. Stay silent when that is the only suppress.
  if (suppressed.every((chart) => chart.bucket === "month")) return null;

  // Name them. "1 of 3 charts" alone tells the visitor something is missing
  // without telling them what, which is the complaint this closes.
  const names = suppressed
    .map((chart) => (chart.title || chart.name || "").trim())
    .filter(Boolean);

  if (!names.length) {
    return `${suppressed.length} of ${declared.length} charts could not be drawn for this table.`;
  }

  return `${listNames(names)} could not be drawn for this table.`;
}

/** "a", "a and b", "a, b and c" — Oxford-free, matching the app's copy. */
function listNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
